import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Code-built furniture, merged: every box, cylinder and tube of one
 * material becomes one geometry, so a run of benches with its cabinets,
 * worktops and taps is a handful of draw calls, not hundreds. UVs in metres,
 * so a texture tiles alike on every piece; a colour per piece.
 */
export class Parts {
  readonly #pieces = new Map<string, THREE.BufferGeometry[]>();

  add(
    key: string,
    geometry: THREE.BufferGeometry,
    at: THREE.Matrix4,
    colour: THREE.ColorRepresentation = 0xffffff,
  ): void {
    const piece = geometry.index ? geometry.toNonIndexed() : geometry;
    piece.applyMatrix4(at);
    for (const name of Object.keys(piece.attributes)) {
      if (!['position', 'normal', 'uv'].includes(name)) piece.deleteAttribute(name);
    }
    if (!piece.getAttribute('uv')) {
      piece.setAttribute(
        'uv',
        new THREE.BufferAttribute(new Float32Array(piece.getAttribute('position').count * 2), 2),
      );
    }
    const c = new THREE.Color(colour);
    const count = piece.getAttribute('position').count;
    const colours = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) colours.set([c.r, c.g, c.b], i * 3);
    piece.setAttribute('color', new THREE.BufferAttribute(colours, 3));
    this.#pieces.set(key, [...(this.#pieces.get(key) ?? []), piece]);
  }

  /** A box `size` metres, its middle at `pos`, turned `turn` degrees about y. */
  box(
    key: string,
    size: readonly [number, number, number],
    pos: readonly [number, number, number],
    colour?: THREE.ColorRepresentation,
    turn = 0,
  ): void {
    const geometry = new THREE.BoxGeometry(...size);
    metreUvs(geometry, size);
    this.add(key, geometry, place(pos, turn), colour);
  }

  /** A box `size` metres placed by any matrix (tilted roof slopes, a turned building's parts). */
  boxAt(
    key: string,
    size: readonly [number, number, number],
    at: THREE.Matrix4,
    colour?: THREE.ColorRepresentation,
  ): void {
    const geometry = new THREE.BoxGeometry(...size);
    metreUvs(geometry, size);
    this.add(key, geometry, at, colour);
  }

  /**
   * A gable's triangle, `width` wide and `height` high, `depth` thick along z:
   * its base on y = 0, centred on x and z. UVs in metres, like the boxes.
   */
  prism(
    key: string,
    width: number,
    height: number,
    depth: number,
    at: THREE.Matrix4,
    colour?: THREE.ColorRepresentation,
  ): void {
    const w = width / 2;
    const d = depth / 2;
    const shape = new THREE.Shape([
      new THREE.Vector2(-w, 0),
      new THREE.Vector2(w, 0),
      new THREE.Vector2(0, height),
    ]);
    const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
    geometry.translate(0, 0, -d);
    // Laid from the front, in metres.
    const position = geometry.getAttribute('position');
    const uv = geometry.getAttribute('uv');
    for (let i = 0; i < uv.count; i++)
      uv.setXY(i, position.getX(i) + position.getZ(i), position.getY(i));
    this.add(key, geometry, at, colour);
  }

  cylinder(
    key: string,
    radius: number,
    height: number,
    pos: readonly [number, number, number],
    colour?: THREE.ColorRepresentation,
    segments = 12,
    top = radius,
  ): void {
    this.add(key, new THREE.CylinderGeometry(top, radius, height, segments), place(pos, 0), colour);
  }

  /** A curved pipe: a tap's gooseneck. */
  torus(
    key: string,
    radius: number,
    tube: number,
    arc: number,
    pos: readonly [number, number, number],
    turn: number,
    colour?: THREE.ColorRepresentation,
  ): void {
    const geometry = new THREE.TorusGeometry(radius, tube, 6, 12, arc);
    this.add(key, geometry, place(pos, turn), colour);
  }

  /** One mesh per material key that has pieces. */
  meshes(materials: Record<string, THREE.Material>): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    for (const [key, pieces] of this.#pieces) {
      const material = materials[key];
      if (!material) throw new Error(`no material for ${key}`);
      const merged = mergeGeometries(pieces, false);
      if (!merged) throw new Error(`could not merge ${key}`);
      const mesh = new THREE.Mesh(merged, material);
      mesh.name = key;
      out.push(mesh);
    }
    return out;
  }
}

function place(pos: readonly [number, number, number], turn: number): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    new THREE.Vector3(...pos),
    new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      THREE.MathUtils.degToRad(turn),
    ),
    new THREE.Vector3(1, 1, 1),
  );
}

/** A box's UVs scaled to metres, so textures tile at the same size on every face. */
function metreUvs(geometry: THREE.BoxGeometry, size: readonly [number, number, number]): void {
  const uv = geometry.getAttribute('uv');
  const [w, h, d] = size;
  // BoxGeometry's faces, two triangles and four vertices each: +x, −x, +y, −y, +z, −z.
  const faces: [number, number][] = [
    [d, h],
    [d, h],
    [w, d],
    [w, d],
    [w, h],
    [w, h],
  ];
  for (let f = 0; f < 6; f++) {
    for (let v = 0; v < 4; v++) {
      const i = f * 4 + v;
      uv.setXY(i, uv.getX(i) * faces[f]![0], uv.getY(i) * faces[f]![1]);
    }
  }
}
